
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Charts from "../SummaryTab/Charts";
import PivotTable from "../SummaryTab/PivotTable";
import heartLoading from "../../assests/heartLoading.gif";
import SavedFilters from "./SavedFilters";

const SavedSummary = ({
  dashboardId,
  currentFilters = {},
  onFiltersChange,
  searchTerm = "",
  onSearchChange,
  productId,
}) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePeriod, setActivePeriod] = useState("");
  const initialLoadDone = useRef(false);
  const hasPivotGroupingRef = useRef(false);

  useEffect(() => {
    initialLoadDone.current = false;
    hasPivotGroupingRef.current = false;
  }, [dashboardId]);

  const excludedColumns = [
    "rule_name",
    "rule_phase",
    "rule_description",
    "total_row_count",
    "error_record_count",
    "validity",
    "accuracy",
    "consistency",
    "completeness",
    "freshness",
    "uniqueness"
  ];

  useEffect(() => {
    if (!dashboardId) return;

    const fetchSavedSummary = async () => {
      setLoading(true);
      try {

        const isInitialLoad = !initialLoadDone.current;
        const hasPivotGrouping = hasPivotGroupingRef.current ||
          (currentFilters?.pivot_grouping &&
            Object.values(currentFilters.pivot_grouping).some(val => val && val.trim() !== ""));

        const effectivePageSize = (isInitialLoad || hasPivotGrouping) ? 10000 : pageSize;
        const effectivePage = (isInitialLoad || hasPivotGrouping) ? 1 : page;

        const res = await axios.get(
          `${BASE_URL}/dashboard-view/summary/${dashboardId}?page=${effectivePage}&page_size=${effectivePageSize}`
        );


        const responseHasPivotGrouping = res.data.pivot_grouping &&
          Object.values(res.data.pivot_grouping).some(val => val && val.trim() !== "");

        if (responseHasPivotGrouping) {
          hasPivotGroupingRef.current = true;
        }

        setData(res.data);

        if (!initialLoadDone.current) {
          const savedFilterState = {
            ...(res.data.filters || {}),
            pivot_grouping: res.data.pivot_grouping || {}
          };

          Object.keys(savedFilterState.pivot_grouping).forEach((key) => {
            if (key.startsWith("Hierachy")) {
              const corrected = key.replace("Hierachy", "Hierarchy");
              savedFilterState.pivot_grouping[corrected] =
                savedFilterState.pivot_grouping[key];
              delete savedFilterState.pivot_grouping[key];
            }
          });

          onFiltersChange(savedFilterState);

          if (res.data.search_term) onSearchChange(res.data.search_term);
          if (res.data.period) {
            setActivePeriod(res.data.period);
          }

          initialLoadDone.current = true;
        }

      } catch (err) {
        console.error("Error fetching saved summary:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedSummary();
  }, [dashboardId, page, pageSize,]);

  if (!data || loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center w-full h-full">
        <div className="flex flex-col items-center">
          <img src={heartLoading} alt="Loading..."style={{ width : 200}}/>
          <p className="text-gray-700 text-lg mt-2">Loading saved view...</p>
        </div>
      </div>
    );
  }

  const hasPivotGrouping = data?.pivot_grouping &&
    Object.values(data.pivot_grouping).some(val => val && val.trim() !== "");

  const paginationData = hasPivotGrouping
    ? { ...data.pagination, totalPages: 1, currentPage: 1 }
    : data.pagination;

  return (
    <div className="summary">
      <SavedFilters savedFilters={data.filters}
        activePeriod={activePeriod}
        showPeriod={true}
      />

      {data?.dqMetrics && (
        <Charts scores={data.dqMetrics} period={activePeriod} />
      )}
      <PivotTable
        tableData={data.tableData}
        paginationData={paginationData}
        filterList={Object.keys(data.tableData?.[0] || {}).filter(
          key => !excludedColumns.includes(key)
        )}
        pivotGrouping={data.pivot_grouping || {}}
        apiLoading={loading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onGroupingChange={(updated) => {
          onFiltersChange({
            ...currentFilters,
            pivot_grouping: updated
          });
        }}
        productId={productId}
        currentFilters={currentFilters}
        page={page}
        searchTerm={searchTerm}
        readOnlyPivot={true}
        dashboardId={dashboardId}
        activePeriod={activePeriod}
      />
    </div>
  );
};

export default SavedSummary;
